import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Scale, Shield, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const DisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="disclaimer-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
        <div className="container-app py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Legal Disclaimer
            </h1>
            <p className="mt-4 text-slate-300 text-lg">
              Important information about using LegalHub services
            </p>
          </motion.div>
        </div>
      </div>
      
      <div className="container-app py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Main Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-8"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-600 shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-amber-800 mb-3">Important Notice</h2>
                <p className="text-amber-900 leading-relaxed font-medium">
                  LegalHub is NOT a law firm and does NOT provide legal advice. We are a technology 
                  platform that connects users with independent legal professionals. Any information 
                  provided on this website is for general informational purposes only and should NOT 
                  be construed as legal advice.
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Platform Role */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-8 border border-slate-200"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <Scale className="w-6 h-6 text-blue-600" />
              Role of LegalHub
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                LegalHub operates as an intermediary platform under the Information Technology Act, 2000 
                and the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) 
                Rules, 2021. Our role is limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Providing a platform for clients to find and connect with lawyers</li>
                <li>Facilitating booking and payment processes</li>
                <li>Verifying lawyer credentials with Bar Council registration</li>
                <li>Enabling communication between clients and lawyers</li>
              </ul>
              <p className="font-medium text-slate-700">
                We do not employ the lawyers listed on our platform. All lawyers are independent 
                practitioners who operate under their own professional liability.
              </p>
            </div>
          </motion.div>
          
          {/* Independent Lawyers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8 border border-slate-200"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-blue-600" />
              About Lawyers on Our Platform
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                All lawyers listed on LegalHub are required to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Be enrolled with a State Bar Council under the Bar Council of India</li>
                <li>Comply with the Advocates Act, 1961</li>
                <li>Adhere to the Bar Council of India Rules on Professional Standards and Etiquette</li>
                <li>Maintain valid practicing certificates</li>
                <li>Provide accurate information about their qualifications and experience</li>
              </ul>
              <div className="bg-slate-50 rounded-lg p-4 mt-4">
                <p className="text-sm">
                  <strong>Note:</strong> While we verify Bar Council registration, LegalHub does not 
                  guarantee the quality, competence, or outcome of any legal services provided by 
                  lawyers through our platform. Users should exercise their own judgment when 
                  selecting a lawyer.
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* No Legal Advice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-8 border border-slate-200"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-600" />
              No Attorney-Client Relationship
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                Using LegalHub or viewing content on this website does NOT create an attorney-client 
                relationship with LegalHub or any lawyer listed on the platform until:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You have directly engaged a specific lawyer</li>
                <li>The lawyer has explicitly agreed to represent you</li>
                <li>An engagement agreement has been signed</li>
              </ul>
              <p>
                The attorney-client relationship, when established, is solely between you and the 
                lawyer you engage. LegalHub is not a party to this relationship.
              </p>
            </div>
          </motion.div>
          
          {/* Limitation of Liability */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-8 border border-slate-200"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4">Limitation of Liability</h2>
            <div className="space-y-4 text-slate-600">
              <p>
                To the fullest extent permitted by law, LegalHub shall not be liable for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Any legal advice, opinions, or services provided by lawyers on the platform</li>
                <li>Any actions taken or not taken based on information from the platform</li>
                <li>Any outcomes of legal matters handled by lawyers found through our platform</li>
                <li>Any disputes between clients and lawyers</li>
                <li>Any loss, damage, or inconvenience caused by use of our services</li>
                <li>Any technical issues, service interruptions, or data loss</li>
              </ul>
            </div>
          </motion.div>
          
          {/* User Responsibilities */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-blue-50 border border-blue-200 rounded-2xl p-8"
          >
            <h2 className="text-xl font-bold text-blue-900 mb-4">Your Responsibilities</h2>
            <div className="space-y-4 text-blue-800">
              <p>By using LegalHub, you acknowledge and agree that:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You will verify the credentials of any lawyer before engaging their services</li>
                <li>You understand that consultation fees are for the lawyer's time and do not guarantee any specific outcome</li>
                <li>You will provide accurate information to lawyers for proper legal advice</li>
                <li>You are responsible for decisions made based on legal consultations</li>
                <li>You will report any misconduct by lawyers to the appropriate Bar Council</li>
              </ul>
            </div>
          </motion.div>
          
          {/* Jurisdiction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-8 border border-slate-200"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4">Governing Law & Jurisdiction</h2>
            <p className="text-slate-600">
              This disclaimer and your use of LegalHub shall be governed by and construed in accordance 
              with the laws of India. Any disputes arising from or related to the use of our platform 
              shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra, India.
            </p>
          </motion.div>
          
          {/* Contact for Disputes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-slate-100 rounded-2xl p-8 text-center"
          >
            <h3 className="font-bold text-slate-900 mb-2">Questions or Concerns?</h3>
            <p className="text-slate-600 mb-4">
              If you have any questions about this disclaimer or our services, please contact us.
            </p>
            <a 
              href="/contact" 
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </a>
          </motion.div>
          
          {/* Last Updated */}
          <p className="text-center text-sm text-slate-500">
            Last Updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DisclaimerPage;
